/**
 * Key transparency dialog — per-contact public-key rotation audit.
 *
 * Fetches the target's `/api/users/{id}/key-history` and renders a
 * timeline of fingerprints. Combined with the local trust-on-first-use
 * ledger (ghostcomm_seen_fingerprints_{userId}), we surface three
 * possible trust states:
 *
 *   ✓ verified     — fingerprint matches what we first saw
 *   ⚠ new-device   — fingerprint changed, but the local ledger confirms
 *                     it was a user-acknowledged rotation (self-rotated)
 *   ✗ suspicious   — fingerprint changed silently without our ack,
 *                     AND the rotation happened AFTER we first saw them.
 *                     This is the MITM signal.
 *
 * All three states are derived client-side from the server-provided
 * history + our private ledger. The server can't lie about past
 * rotations — it can only add new ones — and if it did the ledger would
 * immediately flag the mismatch.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  KeyRound,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  Check,
  X,
  Clock,
  History,
  Fingerprint,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { setItem as safeSetItem, getMeta } from '../utils/storage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEDGER_PREFIX = 'ghostcomm_seen_fingerprints_';

function loadLedger(userId) {
  try {
    const raw = window.localStorage.getItem(LEDGER_PREFIX + userId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveLedger(userId, ledger) {
  safeSetItem(LEDGER_PREFIX + userId, JSON.stringify(ledger));
}

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function formatFingerprint(short) {
  // 16-char hex → 4 groups of 4 for eyeballing: abcd 1234 ef56 7890
  if (!short) return '';
  return short.match(/.{1,4}/g)?.join(' ') || short;
}

export const KeyTransparencyDialog = ({ isOpen, onClose, peer, isSelf }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const userId = peer?.id;

  useEffect(() => {
    if (!isOpen || !userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const url = isSelf
          ? `${API}/users/me/key-history`
          : `${API}/users/${userId}/key-history`;
        const res = await axios.get(url);
        if (!cancelled) setHistory(res.data);
      } catch (e) {
        if (!cancelled)
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              'Could not load key history'
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, userId, isSelf]);

  // Trust ledger + rotation detection (peer-only).
  const trust = useMemo(() => {
    if (isSelf || !history || !userId) return null;
    const current = history.entries.find((e) => e.is_current);
    if (!current) return null;
    const ledger = loadLedger(userId);
    if (!ledger) {
      // First time seeing this peer's fingerprint — trust-on-first-use.
      return { state: 'first-seen', current: current.fingerprint };
    }
    const trusted = ledger.fingerprint;
    const acknowledged = ledger.acknowledged_fingerprints || [];
    if (current.fingerprint === trusted) {
      return { state: 'verified', current: current.fingerprint };
    }
    if (acknowledged.includes(current.fingerprint)) {
      return {
        state: 'acknowledged-rotation',
        current: current.fingerprint,
        previous: trusted,
      };
    }
    // Silent rotation → potential MITM.
    return {
      state: 'suspicious',
      current: current.fingerprint,
      previous: trusted,
    };
  }, [history, userId, isSelf]);

  const acknowledgeRotation = () => {
    if (!trust || !userId) return;
    const ledger = loadLedger(userId) || { acknowledged_fingerprints: [] };
    const ack = new Set(ledger.acknowledged_fingerprints || []);
    if (ledger.fingerprint) ack.add(ledger.fingerprint);
    ack.add(trust.current);
    saveLedger(userId, {
      ...ledger,
      fingerprint: trust.current,
      acknowledged_at: new Date().toISOString(),
      acknowledged_fingerprints: Array.from(ack),
    });
    toast.success('Key rotation acknowledged');
    // Trigger a re-render by nudging history reference
    setHistory((h) => (h ? { ...h } : h));
  };

  const trustOnFirstUse = () => {
    if (!trust || !userId) return;
    saveLedger(userId, {
      fingerprint: trust.current,
      first_seen_at: new Date().toISOString(),
      acknowledged_fingerprints: [trust.current],
    });
    toast.success('Contact trusted');
    setHistory((h) => (h ? { ...h } : h));
  };

  const copyFingerprint = async (fp) => {
    try {
      await navigator.clipboard.writeText(fp);
      toast.success('Fingerprint copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-lg bg-card"
        data-testid="key-transparency-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-primary" />
            Key transparency
            {peer?.username && (
              <span className="text-slate-400 font-normal">
                · {peer.username}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {isSelf
              ? 'Every time you replace your public key, we record it here. Contacts see the same timeline.'
              : 'The fingerprint timeline lets you detect silent key swaps. A rotation you didn\'t expect is the MITM signal.'}
          </DialogDescription>
        </DialogHeader>

        {/* Trust banner */}
        {!isSelf && trust && (
          <TrustBanner
            trust={trust}
            onAcknowledge={acknowledgeRotation}
            onTrustFirst={trustOnFirstUse}
          />
        )}

        {/* Content */}
        {loading && (
          <div className="py-8 text-center text-sm text-slate-500">
            Loading history…
          </div>
        )}
        {error && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300">
            {error}
          </div>
        )}
        {!loading && !error && history && (
          <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-1" data-testid="key-history-list">
            {history.total === 0 && (
              <div className="py-8 text-center text-sm text-slate-500">
                No key history yet. A fingerprint will appear here the
                first time a public key is uploaded.
              </div>
            )}
            {history.entries
              .slice()
              .reverse()
              .map((entry, idx) => (
                <KeyHistoryRow
                  key={entry.id}
                  entry={entry}
                  isFirst={idx === 0}
                  onCopy={() => copyFingerprint(entry.full_fingerprint)}
                />
              ))}
          </div>
        )}

        <div className="text-xs text-slate-500 pt-2 border-t border-border">
          <span className="font-mono">{history?.total ?? 0}</span> rotation
          {history?.total === 1 ? '' : 's'} · fingerprints are SHA-256 of the
          canonicalised RSA public key.
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TrustBanner = ({ trust, onAcknowledge, onTrustFirst }) => {
  if (trust.state === 'verified') {
    return (
      <div
        className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-3"
        data-testid="trust-banner-verified"
      >
        <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-medium text-emerald-300">
            Fingerprint matches — no key changes since you first saw this contact
          </div>
          <div className="text-xs text-emerald-200/70 font-mono mt-1">
            {formatFingerprint(trust.current)}
          </div>
        </div>
      </div>
    );
  }
  if (trust.state === 'acknowledged-rotation') {
    return (
      <div
        className="p-3 rounded-lg bg-sky-500/10 border border-sky-500/30 flex items-start gap-3"
        data-testid="trust-banner-acknowledged"
      >
        <ShieldCheck className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-medium text-sky-300">
            Known rotation — you've already acknowledged this fingerprint
          </div>
          <div className="text-xs text-sky-200/70 font-mono mt-1">
            Current · {formatFingerprint(trust.current)}
          </div>
        </div>
      </div>
    );
  }
  if (trust.state === 'first-seen') {
    return (
      <div
        className="p-3 rounded-lg bg-slate-500/10 border border-slate-500/30 flex items-start gap-3"
        data-testid="trust-banner-first-seen"
      >
        <ShieldQuestion className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-200">
            First time seeing this contact
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Trust-on-first-use: mark this fingerprint as expected so future
            silent rotations are flagged as suspicious.
          </div>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={onTrustFirst}
            data-testid="trust-on-first-use-btn"
          >
            <Check className="w-3.5 h-3.5 mr-1.5" />
            Trust this fingerprint
          </Button>
        </div>
      </div>
    );
  }
  // suspicious
  return (
    <div
      className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-3"
      data-testid="trust-banner-suspicious"
    >
      <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="text-sm font-bold text-red-300">
          ⚠ Silent key rotation detected
        </div>
        <div className="text-xs text-red-200/80 mt-1 leading-relaxed">
          This contact's public key changed without you acknowledging it.
          Possible causes: they lost their device and reinstalled, OR a
          MITM has replaced their key on the server. Verify out-of-band
          before continuing to send sensitive messages.
        </div>
        <div className="text-[11px] text-red-200/60 font-mono mt-2 space-y-0.5">
          <div>Expected · {formatFingerprint(trust.previous)}</div>
          <div>Now · {formatFingerprint(trust.current)}</div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="mt-2 border-red-500/40 hover:bg-red-500/10"
          onClick={onAcknowledge}
          data-testid="acknowledge-rotation-btn"
        >
          <Check className="w-3.5 h-3.5 mr-1.5" />
          Acknowledge rotation
        </Button>
      </div>
    </div>
  );
};

const KeyHistoryRow = ({ entry, isFirst, onCopy }) => {
  const Icon = entry.is_current ? Fingerprint : History;
  return (
    <div
      className={`p-3 rounded-lg border flex items-start gap-3 ${
        entry.is_current
          ? 'bg-primary/5 border-primary/30'
          : 'bg-secondary/30 border-border'
      }`}
      data-testid="key-history-entry"
    >
      <Icon
        className={`w-4 h-4 shrink-0 mt-1 ${
          entry.is_current ? 'text-primary' : 'text-slate-500'
        }`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <code
            className={`text-sm font-mono tracking-wider ${
              entry.is_current ? 'text-slate-100' : 'text-slate-400'
            }`}
          >
            {formatFingerprint(entry.fingerprint)}
          </code>
          {entry.is_current && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/20 text-primary uppercase tracking-wider font-semibold">
              Current
            </span>
          )}
          {isFirst && !entry.is_current && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-600/30 text-slate-400 uppercase tracking-wider">
              Previous
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Active since {relTime(entry.created_at)}
          </span>
          {entry.replaced_at && (
            <span className="flex items-center gap-1 text-amber-400/70">
              <X className="w-3 h-3" />
              Rotated {relTime(entry.replaced_at)}
            </span>
          )}
        </div>
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="shrink-0 text-xs text-slate-500 hover:text-slate-200"
        onClick={onCopy}
        data-testid="copy-fingerprint-btn"
        title="Copy full 64-char fingerprint"
      >
        Copy
      </Button>
    </div>
  );
};

export default KeyTransparencyDialog;
